import type { Problem } from '@/types';

/* Practice Gym ladder: Failed-Payment Recovery (module m13). Problem-forward, progressive.
   Auto-included via src/data/gym/index.ts; referenced by the `rec` ladder. */
export const recoveryProblems: Problem[] = [
  {
    "id": "rc1",
    "ladder": "rec",
    "pos": 1,
    "stage": "Recognition",
    "lvl": 0,
    "difficulty": "recognition",
    "priority": "required",
    "source": "interview-style",
    "module": "m13",
    "title": "What gets counted in a recovery rate?",
    "concept": ["recovery"],
    "obj": ["Charge", "Customer"],
    "metric": "n/a",
    "edge": ["denominator", "direction"],
    "timed": false,
    "est": "3 min",
    "business": "A PM wants 'recovery rate' for failed payments but it's unclear what the unit and denominator are.",
    "schema": ["charges"],
    "prompt": "No SQL yet. In plain English: define recovery, name the unit and the denominator, and state the two conditions a matching success must meet.",
    "deliverable": "A short explanation: the unit (failure), the denominator (all failures), and the direction + window conditions.",
    "why": "Recovery is ambiguous until you fix the unit and the matching rule; recognise this before SQL.",
    "harder": "First rung — name the failure as the unit and the success conditions.",
    "before": [
      "A success can rescue several earlier failures — so what is the unit?",
      "Does a success before the failure count?"
    ],
    "hints": [
      "Unit = the failed charge; denominator = all failures in the window.",
      "Matching success must be strictly after the failure and within the window."
    ],
    "model": "Recovery is a failed charge being followed by a successful one for the same customer within a window. The unit is the failure — each counted once with a yes/no recovery flag — and the denominator is all failures in the window. The matching success must be strictly after the failure (direction) and within the window (time). One success can recover several failures, so you never count successes as the numerator.",
    "grain": "Conceptual — no output rows.",
    "confusion": "Counting successes, not failures, is the classic recovery-rate error.",
    "explain": "Name the unit, denominator, and the two success conditions aloud.",
    "teaches": "Recognising the failure as the unit and the directional/windowed match.",
    "mode": "SQL"
  },
  {
    "id": "rc2",
    "ladder": "rec",
    "pos": 2,
    "stage": "Mechanical",
    "lvl": 1,
    "difficulty": "easy",
    "priority": "required",
    "source": "pgexercises-style",
    "module": "m13",
    "title": "Count failed charges (last 90 days)",
    "concept": ["recovery"],
    "obj": ["Charge"],
    "metric": "count",
    "edge": [],
    "timed": false,
    "est": "4 min",
    "business": "You need the denominator base: failed charges per merchant in the last 90 days.",
    "schema": ["charges"],
    "prompt": "Return one row per merchant with the count of failed charges in the last 90 days.",
    "deliverable": "One row per merchant: merchant_id, failures.",
    "why": "Failures are the recovery-rate denominator; counting them cleanly is step one.",
    "harder": "Introduces the windowed failure population.",
    "before": ["status='failed' and a 90-day rolling window."],
    "hints": [
      "WHERE status='failed' AND created_at >= NOW() - INTERVAL '90 days'.",
      "GROUP BY merchant_id, COUNT(*)."
    ],
    "solution": "SELECT merchant_id, COUNT(*) AS failures\nFROM charges\nWHERE status='failed'\n  AND created_at >= NOW() - INTERVAL '90 days'\nGROUP BY merchant_id\nORDER BY failures DESC;",
    "grain": "One row per merchant.",
    "confusion": "Failures (not successes) form the recovery denominator.",
    "explain": "State that this is the denominator base for recovery rate.",
    "teaches": "Defining the windowed failure population.",
    "mode": "SQL"
  },
  {
    "id": "rc3",
    "ladder": "rec",
    "pos": 3,
    "stage": "Simple application",
    "lvl": 2,
    "difficulty": "medium",
    "priority": "required",
    "source": "Mode-style",
    "module": "m13",
    "title": "Flag each failure as recovered",
    "concept": ["recovery"],
    "obj": ["Charge", "Customer"],
    "metric": "recovery flag",
    "edge": ["direction", "window"],
    "timed": false,
    "est": "7 min",
    "business": "Mark which recent failed charges were recovered by a later successful payment.",
    "schema": ["charges"],
    "prompt": "For failures in the last 90 days, return charge_id and a boolean <code class='inline'>recovered</code> = a succeeded charge for the same customer strictly after the failure and within 7 days.",
    "deliverable": "One row per failed charge: charge_id, recovered.",
    "why": "The per-failure recovery flag via EXISTS is the core building block of the rate.",
    "harder": "Introduces the correlated EXISTS with both direction and window guards.",
    "before": [
      "The success must satisfy: same customer, succeeded, after the failure, within 7 days.",
      "EXISTS returns a clean yes/no without fan-out."
    ],
    "hints": [
      "EXISTS (SELECT 1 FROM charges s WHERE s.customer_id=f.customer_id AND s.status='succeeded' AND s.created_at > f.created_at AND s.created_at <= f.created_at + INTERVAL '7 days').",
      "Keep both bounds: strictly after, and within 7 days."
    ],
    "solution": "SELECT f.charge_id,\n       EXISTS (\n         SELECT 1 FROM charges s\n         WHERE s.customer_id = f.customer_id\n           AND s.status='succeeded'\n           AND s.created_at >  f.created_at\n           AND s.created_at <= f.created_at + INTERVAL '7 days'\n       ) AS recovered\nFROM charges f\nWHERE f.status='failed'\n  AND f.created_at >= NOW() - INTERVAL '90 days';",
    "verify": {
      "grain": "One row per failed charge.",
      "columns": ["charge_id", "recovered"],
      "sample": { "cols": ["charge_id", "recovered"], "rows": [["91001", "true"], ["91002", "false"]] },
      "commonWrong": ["Dropping the direction guard (matching earlier successes).", "Dropping the window (any later success counts)."],
      "validation": ["recovered is boolean per failure", "no row multiplication (EXISTS, not JOIN)"],
      "edgeCases": ["A failure with a success on exactly day 7 — <= includes it; state the boundary.", "A failure with no later success → false."],
      "checklist": ["EXISTS used", "direction guard", "7-day window", "one row per failure"]
    },
    "confusion": "EXISTS avoids the fan-out a JOIN to successes would cause.",
    "explain": "Read the four EXISTS conditions aloud.",
    "teaches": "The per-failure recovery flag via correlated EXISTS.",
    "mode": "SQL"
  },
  {
    "id": "rc4",
    "ladder": "rec",
    "pos": 4,
    "stage": "Applied metric",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "required",
    "source": "DataLemur-style",
    "module": "m13",
    "title": "Recovery rate per merchant",
    "concept": ["recovery"],
    "obj": ["Charge", "Customer"],
    "metric": "recovery rate",
    "edge": ["denominator", "zero denominator"],
    "timed": false,
    "est": "8 min",
    "business": "Recovery wants each merchant's recovery rate over the last 90 days.",
    "schema": ["charges"],
    "prompt": "Per merchant, return failures, recovered (success within 7 days after), and recovery_rate.",
    "deliverable": "One row per merchant: merchant_id, failures, recovered, recovery_rate.",
    "why": "The recovery rate per merchant is the deliverable; denominator discipline is the test.",
    "harder": "Aggregates the per-failure flag into a guarded rate per merchant.",
    "before": ["Denominator = all failures; numerator = recovered failures.", "Guard the divide with NULLIF."],
    "howto": [
      "failures CTE in the window.",
      "Flag each via EXISTS in a flagged CTE.",
      "COUNT(*) FILTER (WHERE recovered) / NULLIF(COUNT(*),0) per merchant."
    ],
    "hints": [
      "Reuse the EXISTS flag, then aggregate per merchant.",
      "recovery_rate = recovered / failures, rounded and guarded."
    ],
    "solution": "WITH failures AS (\n  SELECT charge_id, merchant_id, customer_id, created_at\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '90 days'\n),\nflagged AS (\n  SELECT f.merchant_id,\n         EXISTS (\n           SELECT 1 FROM charges s\n           WHERE s.customer_id=f.customer_id AND s.status='succeeded'\n             AND s.created_at > f.created_at\n             AND s.created_at <= f.created_at + INTERVAL '7 days'\n         ) AS recovered\n  FROM failures f\n)\nSELECT merchant_id,\n       COUNT(*) AS failures,\n       COUNT(*) FILTER (WHERE recovered) AS recovered,\n       ROUND(COUNT(*) FILTER (WHERE recovered)::numeric / NULLIF(COUNT(*),0), 4) AS recovery_rate\nFROM flagged\nGROUP BY merchant_id\nORDER BY recovery_rate DESC;",
    "verify": {
      "grain": "One row per merchant with failures.",
      "columns": ["merchant_id", "failures", "recovered", "recovery_rate"],
      "sample": { "cols": ["merchant_id", "failures", "recovered", "recovery_rate"], "rows": [["101", "320", "208", "0.6500"], ["108", "540", "270", "0.5000"]] },
      "commonWrong": ["recovered ÷ successes instead of ÷ failures.", "Counting successes as recovered."],
      "validation": ["recovered ≤ failures", "recovery_rate ∈ [0,1]"],
      "edgeCases": ["A merchant with failures but zero recoveries → rate 0.", "Window choice changes the rate."],
      "checklist": ["failures denominator", "EXISTS flag", "guarded rate"]
    },
    "confusion": "The denominator is failures; recovered is the subset with a later success in the window.",
    "explain": "Name the denominator and numerator before aggregating.",
    "teaches": "Aggregating recovery flags into a per-merchant rate.",
    "mode": "SQL"
  },
  {
    "id": "rc5",
    "ladder": "rec",
    "pos": 5,
    "stage": "Multi-step",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "should",
    "source": "Mode-style",
    "module": "m13",
    "title": "Next success after each failure (window approach)",
    "concept": ["recovery"],
    "obj": ["Charge", "Customer"],
    "metric": "time to recovery",
    "edge": ["ordering", "window"],
    "timed": false,
    "est": "10 min",
    "business": "Beyond a flag, find when each failure recovered, to study time-to-recovery.",
    "schema": ["charges"],
    "prompt": "For each failed charge in the last 90 days, return its next succeeded charge time for the same customer and a recovered flag if that next success is within 7 days.",
    "deliverable": "One row per failed charge: charge_id, next_success_at, recovered.",
    "why": "The 'next success' framing unlocks time-to-recovery and shows the ordered/window-function alternative to EXISTS.",
    "harder": "Finds the earliest later success (MIN/ordered) instead of a boolean existence.",
    "before": [
      "Next success = MIN(succeeded created_at) for the customer that is after the failure.",
      "recovered = next_success_at within 7 days of the failure."
    ],
    "howto": [
      "failures CTE in the window.",
      "Correlated MIN subquery for the earliest later success per failure.",
      "Flag recovered by comparing that time to the 7-day bound."
    ],
    "hints": [
      "(SELECT MIN(s.created_at) FROM charges s WHERE s.customer_id=f.customer_id AND s.status='succeeded' AND s.created_at > f.created_at).",
      "recovered = next_success_at IS NOT NULL AND next_success_at <= created_at + INTERVAL '7 days'."
    ],
    "solution": "WITH failures AS (\n  SELECT charge_id, customer_id, created_at\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '90 days'\n),\nnext_success AS (\n  SELECT f.charge_id, f.created_at,\n         (SELECT MIN(s.created_at)\n          FROM charges s\n          WHERE s.customer_id = f.customer_id\n            AND s.status='succeeded'\n            AND s.created_at > f.created_at) AS next_success_at\n  FROM failures f\n)\nSELECT charge_id,\n       next_success_at,\n       (next_success_at IS NOT NULL\n        AND next_success_at <= created_at + INTERVAL '7 days') AS recovered\nFROM next_success;",
    "verify": {
      "grain": "One row per failed charge.",
      "columns": ["charge_id", "next_success_at", "recovered"],
      "commonWrong": ["Taking any later success, not the earliest, then mis-measuring time-to-recovery.", "Forgetting the after-failure direction."],
      "validation": ["next_success_at > created_at or NULL", "recovered true only when within 7 days"],
      "edgeCases": ["No later success → next_success_at NULL, recovered false.", "Next success exactly at +7 days is recovered."],
      "checklist": ["earliest later success", "direction guard", "windowed recovered flag"]
    },
    "confusion": "Use the EARLIEST later success (MIN) so time-to-recovery is well defined.",
    "explain": "Explain why MIN of later successes gives the recovery time.",
    "teaches": "Computing next-success and time-to-recovery per failure.",
    "mode": "SQL"
  },
  {
    "id": "rc6",
    "ladder": "rec",
    "pos": 6,
    "stage": "Debug",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "should",
    "source": "interview-style",
    "module": "m13",
    "title": "Debug: counting successes as recoveries",
    "concept": ["recovery"],
    "obj": ["Charge"],
    "metric": "recovery rate",
    "edge": ["denominator", "direction"],
    "timed": false,
    "est": "6 min",
    "business": "A 'recovery rate' sometimes exceeds 100% — it's dividing successes by failures.",
    "schema": ["charges"],
    "prompt": "Diagnose the bug in one sentence, then fix it so the numerator is recovered failures.",
    "broken": "SELECT COUNT(*) FILTER (WHERE status='succeeded') AS recovered,\n       COUNT(*) FILTER (WHERE status='failed')    AS failures,\n       ROUND(COUNT(*) FILTER (WHERE status='succeeded')::numeric\n             / NULLIF(COUNT(*) FILTER (WHERE status='failed'),0), 4) AS recovery_rate\nFROM charges\nWHERE created_at >= NOW() - INTERVAL '90 days';",
    "deliverable": "A one-sentence diagnosis + a corrected EXISTS-based recovery rate.",
    "why": "A rate above 1 is the unmistakable sign of counting successes; fixing it is the core recovery skill.",
    "harder": "You must replace a success count with a per-failure recovery flag.",
    "before": ["Recovery is a property of a failure, not a count of successes."],
    "hints": [
      "Successes and failures have no fixed ratio, so successes/failures can exceed 1.",
      "Flag each failure recovered via EXISTS, then count recovered failures."
    ],
    "solution": "WITH failures AS (\n  SELECT charge_id, customer_id, created_at\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '90 days'\n),\nrecovered AS (\n  SELECT f.charge_id FROM failures f\n  WHERE EXISTS (\n    SELECT 1 FROM charges s\n    WHERE s.customer_id=f.customer_id AND s.status='succeeded'\n      AND s.created_at > f.created_at\n      AND s.created_at <= f.created_at + INTERVAL '7 days')\n)\nSELECT (SELECT COUNT(*) FROM failures)  AS failures,\n       (SELECT COUNT(*) FROM recovered) AS recovered,\n       ROUND((SELECT COUNT(*) FROM recovered)::numeric / NULLIF((SELECT COUNT(*) FROM failures),0), 4) AS recovery_rate;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["failures", "recovered", "recovery_rate"],
      "commonWrong": ["Keeping successes as the numerator.", "Dropping the window so old successes inflate recoveries."],
      "validation": ["recovered ≤ failures", "recovery_rate ∈ [0,1]"],
      "edgeCases": ["Many successes but few failures still yields a rate ≤ 1 once corrected."],
      "checklist": ["named successes-as-numerator", "EXISTS recovery flag", "rate ≤ 1"]
    },
    "confusion": "The tell is recovery_rate > 1 — only possible if you counted successes.",
    "explain": "State the successes-as-numerator bug before the fix.",
    "teaches": "Replacing a success count with a recovered-failure count.",
    "mode": "SQL"
  },
  {
    "id": "rc7",
    "ladder": "rec",
    "pos": 7,
    "stage": "Edge-case hard",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "stretch",
    "source": "StrataScratch-style",
    "module": "m13",
    "title": "One success, multiple failures, and window choice",
    "concept": ["recovery"],
    "obj": ["Charge", "Customer"],
    "metric": "recovery rate",
    "edge": ["one-to-many recovery", "window choice"],
    "timed": false,
    "est": "9 min",
    "business": "Some customers fail several times then one retry succeeds; leadership also wants to compare a 3-day vs 7-day window.",
    "schema": ["charges"],
    "prompt": "Compute the overall recovery rate twice — with a 3-day and a 7-day window — making sure each of several failures rescued by one success is counted, and the success is not double-counted.",
    "deliverable": "One row: failures, recovered_3d, recovered_7d, rate_3d, rate_7d.",
    "why": "Multi-failure recoveries and window sensitivity are exactly where recovery metrics are misread.",
    "harder": "You must keep the failure as the unit while varying the window, and reason about a shared success.",
    "before": [
      "Each failure gets its own EXISTS — a shared later success satisfies all of them.",
      "The window only changes the time bound, not the unit."
    ],
    "howto": [
      "failures CTE.",
      "Two EXISTS flags differing only in the INTERVAL.",
      "Count recovered failures for each window; guard both rates."
    ],
    "hints": [
      "recovered_3d and recovered_7d are two FILTER counts over the same failures.",
      "A success that rescues 3 failures correctly recovers all 3 — that's not double-counting the success."
    ],
    "solution": "WITH failures AS (\n  SELECT charge_id, customer_id, created_at\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '90 days'\n),\nflagged AS (\n  SELECT f.charge_id,\n         EXISTS (SELECT 1 FROM charges s WHERE s.customer_id=f.customer_id AND s.status='succeeded'\n                   AND s.created_at > f.created_at AND s.created_at <= f.created_at + INTERVAL '3 days') AS rec_3d,\n         EXISTS (SELECT 1 FROM charges s WHERE s.customer_id=f.customer_id AND s.status='succeeded'\n                   AND s.created_at > f.created_at AND s.created_at <= f.created_at + INTERVAL '7 days') AS rec_7d\n  FROM failures f\n)\nSELECT COUNT(*) AS failures,\n       COUNT(*) FILTER (WHERE rec_3d) AS recovered_3d,\n       COUNT(*) FILTER (WHERE rec_7d) AS recovered_7d,\n       ROUND(COUNT(*) FILTER (WHERE rec_3d)::numeric / NULLIF(COUNT(*),0), 4) AS rate_3d,\n       ROUND(COUNT(*) FILTER (WHERE rec_7d)::numeric / NULLIF(COUNT(*),0), 4) AS rate_7d\nFROM flagged;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["failures", "recovered_3d", "recovered_7d", "rate_3d", "rate_7d"],
      "commonWrong": ["Joining to successes and counting, which double-counts a shared success across its failures incorrectly.", "Assuming a 3-day window recovers more than 7-day."],
      "validation": ["recovered_3d ≤ recovered_7d ≤ failures", "both rates ∈ [0,1]"],
      "edgeCases": ["One success on day 5 rescues a day-1 and day-4 failure in the 7-day but not the 3-day window for the day-1 failure.", "A success exactly at the bound is included."],
      "checklist": ["failure is the unit", "two windows", "shared success handled", "guards"]
    },
    "confusion": "A success rescuing several failures recovers each of them — that is correct, not double counting.",
    "explain": "Explain why widening the window can only raise the recovery rate.",
    "teaches": "Window sensitivity and one-success-many-failures recovery.",
    "mode": "SQL"
  },
  {
    "id": "rc8",
    "ladder": "rec",
    "pos": 8,
    "stage": "Final boss",
    "lvl": 5,
    "difficulty": "final-boss",
    "priority": "boss",
    "source": "Stripe-interview-style",
    "module": "m13",
    "title": "Final boss (timed 12 min): recovery rate by failure week",
    "concept": ["recovery"],
    "obj": ["Charge", "Customer"],
    "metric": "recovery rate",
    "edge": ["immature window", "denominator", "direction"],
    "timed": true,
    "est": "12 min",
    "business": "\"Is recovery improving week over week?\" Build the trend and defend the denominator and the immature-week caveat.",
    "schema": ["charges"],
    "prompt": "Per week of failure (bucket on the failure's created_at, last 8 weeks), report failures, recovered (success within 7 days after, same customer), and recovery_rate. Keep failures as the denominator, count each failure once, and flag that the newest week is immature.",
    "deliverable": "One row per failure week: fail_week, failures, recovered, recovery_rate.",
    "why": "This fuses bucketing, the directional/windowed recovery flag, denominator discipline and maturity judgment — a complete recovery interview answer.",
    "harder": "Combines weekly bucketing, the EXISTS recovery flag, a fixed denominator and an immature-week caveat under time pressure.",
    "before": [
      "Bucket failures with DATE_TRUNC('week', created_at).",
      "recovered via EXISTS (later success within 7 days); failures is always the denominator.",
      "The newest week's failures haven't had 7 days to recover — say so."
    ],
    "howto": [
      "failures CTE with a fail_week bucket.",
      "Flag each via EXISTS in a flagged CTE.",
      "Aggregate per week; guard the rate."
    ],
    "hints": [
      "DATE_TRUNC('week', created_at) for the bucket.",
      "Recovery flag identical to earlier rungs; aggregate per fail_week.",
      "Mention the most recent week is provisional."
    ],
    "solution": "WITH failures AS (\n  SELECT charge_id, customer_id, created_at,\n         DATE_TRUNC('week', created_at) AS fail_week\n  FROM charges\n  WHERE status='failed' AND created_at >= NOW() - INTERVAL '8 weeks'\n),\nflagged AS (\n  SELECT f.fail_week,\n         EXISTS (\n           SELECT 1 FROM charges s\n           WHERE s.customer_id=f.customer_id AND s.status='succeeded'\n             AND s.created_at > f.created_at\n             AND s.created_at <= f.created_at + INTERVAL '7 days'\n         ) AS recovered\n  FROM failures f\n)\nSELECT fail_week,\n       COUNT(*) AS failures,\n       COUNT(*) FILTER (WHERE recovered) AS recovered,\n       ROUND(COUNT(*) FILTER (WHERE recovered)::numeric / NULLIF(COUNT(*),0), 4) AS recovery_rate\nFROM flagged\nGROUP BY fail_week\nORDER BY fail_week;",
    "rubric": [
      "Failures bucketed by week of failure",
      "recovered via EXISTS: later success within 7 days, same customer",
      "Denominator is all failures in the week (each counted once)",
      "Guards (NULLIF) on the rate",
      "Newest week flagged immature (its failures may still recover)",
      "No success counted as the numerator"
    ],
    "verify": {
      "grain": "One row per failure week.",
      "columns": ["fail_week", "failures", "recovered", "recovery_rate"],
      "sample": { "cols": ["fail_week", "failures", "recovered", "recovery_rate"], "rows": [["2024-05-06", "210", "140", "0.6667"], ["2024-05-13", "198", "120", "0.6061"]] },
      "commonWrong": ["Bucketing on the success date.", "Counting successes as recovered.", "Treating the newest week as final."],
      "validation": ["recovered ≤ failures per week", "recovery_rate ∈ [0,1]"],
      "edgeCases": ["The most recent week is immature.", "A failure at week's end with a success early next week still counts as recovered."],
      "checklist": ["weekly failure bucket", "EXISTS flag", "failure denominator", "immature-week caveat"]
    },
    "confusion": "Bucket on the failure week, not the success; the newest week is provisional.",
    "explain": "Defend the bucket, the denominator and the immature-week caveat as if to an interviewer.",
    "teaches": "A complete weekly recovery-rate trend with maturity judgment.",
    "mode": "SQL"
  }
];
